import * as React from 'react';
import {
  Box,
  createTheme,
  SxProps,
  Theme,
  ThemeProvider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  gridClasses,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import { GOLD_1, TABLE_ROW_ALT } from '@themes/ui/escoffier';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

interface Props extends DataGridProProps {
  customHeaderColor?: string;
  customRowAltColor?: string;
  containerHeight?: string;
  styles?: SxProps<Theme>;
}

export const CustomToolbar: React.FC = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport
        csvOptions={{
          allColumns: true,
        }}
        printOptions={{ disableToolbarButton: true }}
      />
    </GridToolbarContainer>
  );
};

// Conditionally rendered toolbar that only shows when there's data
const ConditionalToolbar: React.FC<{ hasData: boolean; slots: any }> = ({ hasData, slots }) => {
  if (!hasData) {
    return null;
  }
  
  const ToolbarComponent = slots?.toolbar;
  return ToolbarComponent ? <ToolbarComponent /> : <CustomToolbar />;
};

const EcDataGrid: React.FC<Props> = ({
  customHeaderColor = GOLD_1,
  customRowAltColor = TABLE_ROW_ALT,
  containerHeight = 'auto',
  styles,
  ...dataGridProps
}) => {
  const theme = useTheme();
  const hasData = Boolean(dataGridProps.rows && dataGridProps.rows.length > 0);

  return (
    <ThemeProvider
      theme={createTheme({
        palette: theme.palette,
        typography: theme.typography,
      })}
    >
      <Box sx={{ width: '100%', height: containerHeight }}>
        <DataGridPro
          {...dataGridProps}
          disableRowSelectionOnClick
          getRowClassName={(params): string =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          slots={{
            ...dataGridProps.slots,
            detailPanelExpandIcon: KeyboardArrowRight,
            detailPanelCollapseIcon: KeyboardArrowDown,
            toolbar: () => <ConditionalToolbar hasData={hasData} slots={dataGridProps.slots} />
          }}
          showCellVerticalBorder={true}
          showColumnVerticalBorder={true}
          columnHeaderHeight={64}
          sx={{
            ...styles,
            '& .MuiCheckbox-root svg': {
              color: theme.palette.GRAY_3.main,
            },
            '.MuiDataGrid-columnHeader': {
              backgroundColor: customHeaderColor,
              display: hasData ? 'flex' : 'none',
            },
            '.MuiDataGrid-columnHeaderTitleContainer': {
              display: 'flex',
              justifyContent: 'space-between',
              width: 'calc(100% - 3em)',
              padding: '0 0.2em',
              margin: '0',
              lineHeight: 'normal',
              whiteSpace: 'normal',
              flex: 1,
            },
            '.MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-iconButtonContainer': {
              visibility: 'visible !important',
            },
            '.MuiDataGrid-columnHeaderTitle': {
              ...(theme as Theme).typography.EC_TYPE_XL,
              color: theme.palette.WHITE.main,
              fontWeight: 800,
              textTransform: 'uppercase',
              whiteSpace: 'normal',
              lineHeight: '1.2',
              transition: 'font-size 0.2s ease',
              fontSize: '0.95em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '.MuiDataGrid-columnHeader:hover .MuiDataGrid-columnHeaderTitle': {
              fontSize: '0.85em',
            },
            '& .MuiDataGrid-sortIcon, .MuiDataGrid-menuIconButton': {
              opacity: 'inherit !important',
              color: theme.palette.WHITE.main,
              ':hover': {
                opacity: 1,
              },
            },
            '.MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
              marginLeft: '-1em',
              padding: '0',
            },
            // '& .MuiDataGrid-iconButtonContainer': {
            //   marginRight: '-0.5em',
            // },
            [`& .${gridClasses.row}.odd`]: {
              backgroundColor: customRowAltColor,
              '&.Mui-selected': {
                backgroundColor: 'rgba(187, 17, 51, 0.08)',
              },
              ':hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: `2px solid ${theme.palette.divider}`,
              display: hasData ? 'flex' : 'none',
              height: hasData ? 'auto' : '0px !important',
              minHeight: hasData ? 'auto' : '0px !important',
              maxHeight: hasData ? 'auto' : '0px !important',
              overflow: hasData ? 'visible' : 'hidden',
            },
            '& .MuiDataGrid-toolbarContainer': {
              display: hasData ? 'flex' : 'none',
              height: hasData ? 'auto' : '0px',
              minHeight: hasData ? 'auto' : '0px',
              maxHeight: hasData ? 'auto' : '0px',
              overflow: hasData ? 'visible' : 'hidden',
            },
            '& .MuiDataGrid-columnSeparator': {
              color: theme.palette.divider,
              opacity: 1,
              '&.MuiDataGrid-columnSeparator--sideRight': {
                right: -12,
              },
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnSeparator': {
              color: theme.palette.WHITE.main,
              opacity: 0.5,
            },
            '& .MuiDataGrid-columnHeader': {
              padding: '8px 4px',
            },
            '& .MuiDataGrid-columnHeader:first-of-type .MuiDataGrid-columnHeaderTitleContainer': {
              padding: '0',
              justifyContent: 'center',
            },
            '& .MuiDataGrid-columnHeadersInner': {
              width: '100% !important',
            },
            '& .MuiDataGrid-cell': {
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            },
            // When no data, display the no rows overlay with more top margin since headers are hidden
            '& .MuiDataGrid-main .MuiDataGrid-overlay': {
              top: hasData ? 'auto' : '0',
              marginTop: hasData ? 'auto' : '3em',
            },
          }}
          data-testid="ec-data-grid"
        />
      </Box>
    </ThemeProvider>
  );
};

export default EcDataGrid;
